import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SignIn, UserPlus, MagnifyingGlass, MapPin, Eye, EyeSlash } from "@phosphor-icons/react";
import { DateField } from "@/components/DateField";
import SearchMap from "@/components/SearchMap";
import MarinaInquiryDialog from "@/components/MarinaInquiryDialog";
import TrackInquiryDialog from "@/components/TrackInquiryDialog";

function formatErr(detail) {
  if (detail == null) return "Κάτι πήγε στραβά. Δοκιμάστε ξανά.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Login() {
  const { login, register } = useAuth();
  const [showLogin, setShowLogin] = useState(false);
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const isRegister = mode === "register";

  const [area, setArea] = useState("");
  const [len, setLen] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchErr, setSearchErr] = useState("");
  const [lastArea, setLastArea] = useState("");
  const [facilityType, setFacilityType] = useState("sea");
  const [inquiryMarina, setInquiryMarina] = useState(null);
  const [showTrack, setShowTrack] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isRegister) await register(email.trim(), password, name.trim());
      else await login(email.trim(), password);
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const runSearch = async (ignoreArea) => {
    setSearchErr("");
    if (!len || Number(len) <= 0) return setSearchErr("Συμπληρώστε το μήκος του σκάφους.");
    if (!startDate || !endDate) return setSearchErr("Επιλέξτε περίοδο (Από και Έως).");
    if (endDate < startDate) return setSearchErr("Η ημ/νία λήξης δεν μπορεί να είναι πριν την έναρξη.");
    setSearching(true);
    try {
      const params = { length: Number(len), start_date: startDate, end_date: endDate, facility_type: facilityType };
      const useArea = !ignoreArea && area.trim();
      if (useArea) params.area = area.trim();
      setLastArea(useArea ? area.trim() : "");
      const res = (await api.get("/public/search", { params })).data;
      setResults(res);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const doSearch = (e) => { e.preventDefault(); runSearch(false); };
  const searchNearby = () => runSearch(true);

  return (
    <div className="relative flex min-h-screen overflow-hidden">
      <img
        src="/login-hero.webp"
        alt=""
        className="absolute inset-0 h-full w-full object-cover"
        style={{ filter: "brightness(0.9) saturate(1.05)", objectPosition: "center 62%" }}
      />
      <div className="absolute inset-0" style={{ background: "rgba(20,44,74,0.12)" }} />
      <div className="relative hidden lg:block lg:w-[38.2%] lg:shrink-0 lg:flex-none">
        <div className="relative flex h-full flex-col justify-between p-12 text-white">
          <div className="flex flex-col gap-5">
            <img src="/navipark-logo-white.png" alt="NAVIPARK" className="h-auto w-full max-w-[320px] drop-shadow-[0_2px_10px_rgba(0,0,0,0.55)]" />
            <div className="mt-[calc(20vh-8rem)] max-w-[380px] space-y-5" data-testid="hero-info-cards">
              {[
                { t: "Διαχείριση θέσεων", d: "Κάτοψη εγκατάστασης, διαθεσιμότητα και ιστορικό κάθε θέσης σε πραγματικό χρόνο." },
                { t: "Σκάφη & πελάτες", d: "Καρτέλα σκάφους, στοιχεία ιδιοκτήτη, έγγραφα και χρεώσεις ανά περίοδο ή ανά μέτρο." },
                { t: "Συμβάσεις & πληρωμές", d: "Αυτόματα συμφωνητικά PDF, ψηφιακή υπογραφή, υπενθυμίσεις λήξης και υπόλοιπα." },
                { t: "Κρατήσεις από πελάτες", d: "Δημόσια αναζήτηση εγκατάστασης και αιτήματα κράτησης απευθείας στην εφαρμογή." },
              ].map((c) => (
                <div key={c.t} className="drop-shadow-[0_2px_8px_rgba(0,0,0,0.75)]">
                  <div className="text-base font-bold tracking-wide text-white">{c.t}</div>
                  <p className="mt-0.5 text-sm leading-relaxed text-white/90">{c.d}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="text-base font-bold tracking-wider text-white drop-shadow-[0_1px_6px_rgba(0,0,0,0.6)]">NAVIPARK © 2026</div>
        </div>
      </div>

      <div
        className="relative flex flex-1 items-center justify-center p-6"
        data-testid="landing-right-panel"
      >
        <button
          type="button"
          onClick={() => { setShowLogin((v) => !v); setError(""); }}
          className="absolute right-6 top-6 text-sm font-semibold text-white hover:underline"
          data-testid="admin-login-link"
        >
          {showLogin ? "Επιστροφή" : "Σύνδεση διαχειριστή"}
        </button>

        {showLogin ? (
          <form onSubmit={submit} className="max-h-[92vh] w-full max-w-sm overflow-y-auto rounded-sm bg-white/95 p-7 shadow-xl backdrop-blur-sm" data-testid="login-form">
            <div className="mb-8 lg:hidden">
              <img src="/navipark-logo.png" alt="NAVIPARK" className="h-10 w-auto" />
            </div>
            <h2 className="font-head text-2xl font-extrabold text-[#234E7D]">{isRegister ? "Εγγραφή" : "Σύνδεση διαχειριστή"}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{isRegister ? "Δημιουργήστε λογαριασμό για να ξεκινήσετε." : "Συνδεθείτε για να συνεχίσετε."}</p>

            <div className="mt-6 space-y-4">
              {isRegister && (
                <div>
                  <Label htmlFor="name" className="text-xs font-semibold tracking-wider text-muted-foreground">Όνομα</Label>
                  <Input id="name" value={name} onChange={(e) => setName(e.target.value)} className="mt-2 rounded-sm" data-testid="register-name" autoComplete="name" />
                </div>
              )}
              <div>
                <Label htmlFor="email" className="text-xs font-semibold tracking-wider text-muted-foreground">Email</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 rounded-sm" data-testid="login-email" autoComplete="username" />
              </div>
              <div>
                <Label htmlFor="password" className="text-xs font-semibold tracking-wider text-muted-foreground">Κωδικός</Label>
                <div className="relative">
                  <Input id="password" type={showPass ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 rounded-sm pr-10" data-testid="login-password" autoComplete={isRegister ? "new-password" : "current-password"} />
                  <button type="button" onClick={() => setShowPass((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 mt-1 text-muted-foreground hover:text-[#234E7D]" aria-label={showPass ? "Απόκρυψη κωδικού" : "Εμφάνιση κωδικού"} data-testid="toggle-password">
                    {showPass ? <EyeSlash size={20} weight="bold" /> : <Eye size={20} weight="bold" />}
                  </button>
                </div>
              </div>
            </div>

            {error && (
              <div className="mt-4 rounded-sm border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive" data-testid="login-error">{error}</div>
            )}

            <Button type="submit" disabled={loading} className="mt-6 w-full rounded-sm bg-[#0055FF] hover:bg-[#0044CC]" data-testid="login-submit">
              {loading ? "Παρακαλώ περιμένετε..." : isRegister ? (<><UserPlus size={18} weight="bold" /> Εγγραφή</>) : (<><SignIn size={18} weight="bold" /> Σύνδεση</>)}
            </Button>

            <button
              type="button"
              onClick={() => { setMode(isRegister ? "login" : "register"); setError(""); }}
              className="mt-4 w-full text-center text-sm font-medium text-[#0055FF] hover:underline"
              data-testid="toggle-auth-mode"
            >
              {isRegister ? "Έχετε ήδη λογαριασμό; Σύνδεση" : "Δεν έχετε λογαριασμό; Εγγραφή"}
            </button>
          </form>
        ) : (
          <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-sm bg-white/95 p-7 shadow-xl backdrop-blur-sm" data-testid="berth-search">
            <div className="mb-8 lg:hidden">
              <img src="/navipark-logo.png" alt="NAVIPARK" className="h-10 w-auto" />
            </div>
            <h2 className="font-head text-3xl font-extrabold leading-tight text-[#234E7D]">Αναζητήστε θέση{facilityType === "sea" ? " ελλιμενισμού" : " φύλαξης"}<br />για το σκάφος σας</h2>
            <p className="mt-2 text-sm text-muted-foreground">{facilityType === "sea" ? "Βρείτε διαθέσιμες θέσεις ελλιμενισμού σε μαρίνες ανά περιοχή." : "Βρείτε διαθέσιμες θέσεις χερσαίας φύλαξης ανά περιοχή."}</p>

            <form onSubmit={doSearch} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="area" className="text-xs font-semibold tracking-wider text-muted-foreground">Περιοχή</Label>
                <Input id="area" value={area} onChange={(e) => setArea(e.target.value)} placeholder="π.χ. Λευκάδα ή 31100" className="mt-2 rounded-sm" data-testid="search-area" />
              </div>
              <div>
                <Label htmlFor="len" className="text-xs font-semibold tracking-wider text-muted-foreground">Μήκος σκάφους (μ) *</Label>
                <Input id="len" type="number" min="0" step="0.1" value={len} onChange={(e) => setLen(e.target.value)} placeholder="π.χ. 10" className="mt-2 rounded-sm" data-testid="search-length" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs font-semibold tracking-wider text-muted-foreground">Τύπος θέσης</Label>
                <div className="mt-2 flex gap-2">
                  {[{ v: "sea", l: "Θαλάσσια" }, { v: "land", l: "Χερσαία" }].map((o) => (
                    <button
                      key={o.v}
                      type="button"
                      onClick={() => setFacilityType(o.v)}
                      className={`flex-1 rounded-sm border px-3 py-2 text-sm font-semibold transition-colors ${facilityType === o.v ? "border-[#0055FF] bg-[#0055FF] text-white" : "border-border bg-white text-foreground hover:bg-secondary"}`}
                      data-testid={`search-type-${o.v}`}
                    >
                      {o.l}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold tracking-wider text-muted-foreground">Περίοδος — Από *</Label>
                  <DateField value={startDate} onChange={setStartDate} testId="search-start" />
                </div>
                <div>
                  <Label className="text-xs font-semibold tracking-wider text-muted-foreground">Έως *</Label>
                  <DateField value={endDate} onChange={setEndDate} testId="search-end" />
                </div>
              </div>
              {searchErr && (
                <div className="rounded-sm border border-destructive/30 bg-destructive/5 p-2 text-sm text-destructive" data-testid="search-error">{searchErr}</div>
              )}
              <Button type="submit" disabled={searching} className="w-full rounded-sm bg-[#0055FF] hover:bg-[#0044CC]" data-testid="search-submit">
                {searching ? "Αναζήτηση..." : (<><MagnifyingGlass size={18} weight="bold" /> Αναζήτηση</>)}
              </Button>
            </form>

            <button type="button" onClick={() => setShowTrack(true)} className="mt-3 text-sm font-medium text-[#0055FF] hover:underline" data-testid="track-link">
              Έχετε ήδη αίτημα; Παρακολούθηση με κωδικό
            </button>
            {results && (() => {
              const withAvail = results.filter((r) => r.available > 0);
              if (withAvail.length === 0) {
                return (
                  <div className="mt-6 space-y-3" data-testid="search-results">
                    <p className="text-sm text-muted-foreground" data-testid="search-empty">
                      {lastArea ? `Δεν βρέθηκε διαθέσιμη θέση στην περιοχή «${lastArea}».` : "Δεν βρέθηκε διαθέσιμη θέση για τα κριτήριά σας."}
                    </p>
                    {lastArea && (
                      <Button type="button" variant="outline" className="w-full rounded-sm" onClick={searchNearby} data-testid="search-nearby">
                        <MapPin size={16} weight="bold" /> Αναζήτηση σε κοντινές περιοχές
                      </Button>
                    )}
                  </div>
                );
              }
              return (
                <div className="mt-6 space-y-3" data-testid="search-results">
                  {withAvail.map((r, i) => (
                    <div key={i} className="rounded-sm border border-border p-4" data-testid={`result-${i}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="font-bold text-[#234E7D]">{r.name}</div>
                        <span className="shrink-0 rounded-sm bg-[#DCFCE7] px-2 py-1 text-xs font-bold text-[#065F46]">1 θέση διαθέσιμη</span>
                      </div>
                      <div className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
                        <MapPin size={14} /> {[r.address, r.city, r.postal_code].filter(Boolean).join(", ") || "—"}
                      </div>
                      {(r.phone || r.email) && (
                        <div className="mt-1 text-sm text-foreground">Επικοινωνία: {[r.phone, r.email].filter(Boolean).join(" · ")}</div>
                      )}
                      <Button type="button" variant="outline" className="mt-3 w-full rounded-sm" onClick={() => setInquiryMarina(r)} data-testid={`inquiry-btn-${i}`}>
                        Στοιχεία & αίτημα κράτησης
                      </Button>
                    </div>
                  ))}
                  <SearchMap marinas={withAvail} />
                </div>
              );
            })()}

            <MarinaInquiryDialog
              marina={inquiryMarina}
              context={{ length: len, startDate, endDate }}
              open={!!inquiryMarina}
              onOpenChange={(o) => !o && setInquiryMarina(null)}
            />
            <TrackInquiryDialog open={showTrack} onOpenChange={setShowTrack} />
          </div>
        )}
      </div>
    </div>
  );
}
